import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Loader2, ExternalLink, Check, AlertCircle } from 'lucide-react';
import axios from 'axios';
import debounce from 'lodash.debounce';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImageSearchModal = ({ open, onOpenChange, onSelect, initialQuery = '' }) => {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [apiConfigured, setApiConfigured] = useState(true);

  const searchImages = useCallback(async (searchQuery) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.get(`${API}/search-images`, {
        params: { query: searchQuery, per_page: 20 }
      });
      
      setResults(response.data.results || []);
      setApiConfigured(response.data.api_keys_configured !== false);
      
      if (response.data.errors?.length) {
        console.warn('Image search warnings:', response.data.errors);
      }
    } catch (err) {
      console.error('Image search error:', err);
      setError('Failed to search images. Please try again.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((q) => searchImages(q), 500),
    [searchImages]
  );

  useEffect(() => {
    if (open && initialQuery) {
      setQuery(initialQuery);
      searchImages(initialQuery);
    }
  }, [open, initialQuery, searchImages]);

  const handleQueryChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    debouncedSearch(value);
  };

  const handleSelect = () => {
    if (selectedImage) {
      onSelect(selectedImage.url);
      setSelectedImage(null);
      setQuery('');
      setResults([]);
      onOpenChange(false);
    }
  };

  const handleImageClick = (image) => {
    setSelectedImage(selectedImage?.id === image.id ? null : image);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Search Images</DialogTitle>
        </DialogHeader>
        
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={query}
            onChange={handleQueryChange}
            placeholder="Search for images (e.g., smartphone, watch, perfume)..."
            className="pl-10"
            autoFocus
            data-testid="image-search-input"
          />
        </div>
        
        {/* API Not Configured Warning */}
        {!apiConfigured && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <div>
              <p className="font-medium">Image API not configured</p>
              <p className="text-xs mt-0.5">Add UNSPLASH_ACCESS_KEY or PEXELS_API_KEY to enable image search.</p>
            </div>
          </div>
        )}
        
        {/* Results Grid */}
        <div className="flex-1 overflow-y-auto min-h-[300px]">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-[#0047AB]" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full text-red-500">
              {error}
            </div>
          ) : results.length > 0 ? (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 p-2">
              {results.map((image) => (
                <div
                  key={image.id}
                  onClick={() => handleImageClick(image)}
                  className={`
                    relative aspect-square rounded-lg overflow-hidden cursor-pointer
                    border-2 transition-all
                    ${selectedImage?.id === image.id 
                      ? 'border-[#0047AB] ring-2 ring-[#0047AB]/30 scale-105' 
                      : 'border-transparent hover:border-slate-300'
                    }
                  `}
                  data-testid={`search-result-${image.id}`}
                >
                  <img
                    src={image.thumbnail}
                    alt={image.alt}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  
                  {/* Selected indicator */}
                  {selectedImage?.id === image.id && (
                    <div className="absolute inset-0 bg-[#0047AB]/20 flex items-center justify-center">
                      <div className="w-8 h-8 bg-[#0047AB] rounded-full flex items-center justify-center">
                        <Check className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  )}
                  
                  {/* Source badge */}
                  <div className={`absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-medium ${
                    image.source === 'unsplash' 
                      ? 'bg-black/70 text-white' 
                      : 'bg-teal-600/90 text-white'
                  }`}>
                    {image.source}
                  </div>
                </div>
              ))}
            </div>
          ) : query ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-500">
              <Search className="w-12 h-12 mb-2 text-slate-300" />
              <p>No images found for "{query}"</p>
              <p className="text-sm">Try a different search term</p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-slate-500">
              <Search className="w-12 h-12 mb-2 text-slate-300" />
              <p>Enter a search term to find images</p>
              <p className="text-sm">Powered by Unsplash & Pexels</p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-200">
          <div className="text-xs text-slate-500">
            {selectedImage && (
              <span className="flex items-center gap-1">
                Photo by{' '}
                <a 
                  href={selectedImage.photographer_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#0047AB] hover:underline inline-flex items-center gap-0.5"
                >
                  {selectedImage.photographer}
                  <ExternalLink className="w-3 h-3" />
                </a>
                {' '}on {selectedImage.source}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSelect}
              disabled={!selectedImage}
              className="bg-[#0047AB] hover:bg-[#003380]"
              data-testid="select-image-btn"
            >
              Select Image
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageSearchModal;
